import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    effect,
    HostListener,
    NgZone,
    ViewChild,
    inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
    AI_ASSISTANT_BRAND_IMAGE,
    AI_ASSISTANT_COLLAPSED_SUGGESTIONS,
    AI_ASSISTANT_NAME,
    AI_ASSISTANT_POSITION_KEY,
    AI_ASSISTANT_SUBTITLE,
} from './ai-assistant.constants';
import { AiAssistantService } from './ai-assistant.service';
import { AiAssistantSuggestion } from './ai-assistant.types';

@Component({
    selector: 'app-ai-assistant-host',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './ai-assistant-host.component.html',
    styleUrl: './ai-assistant-host.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiAssistantHostComponent implements AfterViewInit {
    readonly ai = inject(AiAssistantService);
    private readonly zone = inject(NgZone);
    private readonly cdr = inject(ChangeDetectorRef);

    readonly name = AI_ASSISTANT_NAME;
    readonly subtitle = AI_ASSISTANT_SUBTITLE;
    readonly brandImage = AI_ASSISTANT_BRAND_IMAGE;

    inputText = '';

    /** Gợi ý nhanh — mặc định thu gọn. */
    suggestionsExpanded = false;

    /** Vị trí góc trên-trái của nút FAB trên viewport — neo cố định khi panel mở/đóng */
    fabAnchorX = 0;
    fabAnchorY = 0;
    positionX = 0;
    positionY = 0;
    positionReady = false;
    isDragging = false;

    get containerTransform(): string | null {
        if (!this.positionReady) {
            return null;
        }
        return `translate3d(${this.positionX}px, ${this.positionY}px, 0)`;
    }

    toggleSuggestions(): void {
        this.suggestionsExpanded = !this.suggestionsExpanded;
    }

    /** Chip gợi ý hiển thị — thu gọn: 3 đầu; mở rộng: tất cả. */
    visibleSuggestions(): AiAssistantSuggestion[] {
        const all = this.ai.suggestions();
        if (this.suggestionsExpanded) {
            return all;
        }
        return all.slice(0, AI_ASSISTANT_COLLAPSED_SUGGESTIONS);
    }

    /** Số chip còn ẩn khi thu gọn. */
    hiddenSuggestionCount(): number {
        return Math.max(0, this.ai.suggestions().length - AI_ASSISTANT_COLLAPSED_SUGGESTIONS);
    }

    private readonly dragThresholdPx = 5;
    private readonly edgeMarginPx = 8;
    private fabPointerActive = false;
    private dragMoved = false;
    private dragStartClientX = 0;
    private dragStartClientY = 0;
    private dragOriginFabX = 0;
    private dragOriginFabY = 0;
    private layoutFabOffsetX = 0;
    private layoutFabOffsetY = 0;
    private layoutContainerW = 0;
    private layoutContainerH = 0;
    private pendingDragLeft = 0;
    private pendingDragTop = 0;
    private pendingFabX = 0;
    private pendingFabY = 0;
    private dragListenersActive = false;
    private readonly onDocumentPointerMove = (event: PointerEvent) => this.handleDragMove(event);
    private readonly onDocumentPointerEnd = (event: PointerEvent) => this.handleDragEnd(event);

    @ViewChild('assistantRoot') assistantRootRef?: ElementRef<HTMLElement>;
    @ViewChild('fabButton') fabButtonRef?: ElementRef<HTMLButtonElement>;
    @ViewChild('messageList') messageListRef?: ElementRef<HTMLElement>;
    @ViewChild('inputField') inputFieldRef?: ElementRef<HTMLInputElement>;

    constructor() {
        effect(() => {
            this.ai.panelOpen();
            this.ai.showTeaser();
            if (this.positionReady && !this.isDragging) {
                window.setTimeout(() => this.syncContainerPosition(), 0);
            }
        });
    }

    ngAfterViewInit(): void {
        this.initPosition();
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        if (!this.positionReady || this.isDragging) {
            return;
        }
        this.syncContainerPosition();
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.ai.panelOpen()) {
            this.ai.closePanel();
        }
    }

    onFabPointerDown(event: PointerEvent): void {
        if (event.button !== 0) {
            return;
        }
        event.preventDefault();
        this.fabPointerActive = true;
        this.dragMoved = false;
        this.isDragging = false;
        this.dragStartClientX = event.clientX;
        this.dragStartClientY = event.clientY;

        const fabRect = this.fabButtonRef?.nativeElement?.getBoundingClientRect();
        if (fabRect) {
            this.fabAnchorX = fabRect.left;
            this.fabAnchorY = fabRect.top;
        }
        this.dragOriginFabX = this.fabAnchorX;
        this.dragOriginFabY = this.fabAnchorY;
        this.cacheLayoutMetrics();

        this.zone.runOutsideAngular(() => {
            if (!this.dragListenersActive) {
                document.addEventListener('pointermove', this.onDocumentPointerMove);
                document.addEventListener('pointerup', this.onDocumentPointerEnd);
                document.addEventListener('pointercancel', this.onDocumentPointerEnd);
                this.dragListenersActive = true;
            }
        });
    }

    onFabClick(): void {
        if (this.ai.panelOpen()) {
            this.ai.closePanel();
        } else {
            this.ai.openPanel();
            this.focusInputSoon();
        }
    }

    onTeaserClick(): void {
        this.ai.openPanel();
        this.focusInputSoon();
    }

    onDismissTeaser(): void {
        this.ai.dismissTeaser();
    }

    onSend(): void {
        if (!this.inputText.trim()) {
            return;
        }
        this.ai.sendUserMessage(this.inputText);
        this.inputText = '';
        this.scrollMessagesSoon();
    }

    onSuggestionClick(prompt: string): void {
        this.ai.sendUserMessage(prompt);
        this.scrollMessagesSoon();
    }

    onRefresh(): void {
        this.ai.resetConversation();
        this.suggestionsExpanded = false;
        this.scrollMessagesSoon();
    }

    formatMessage(text: string): string {
        return text
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            .replace(/\n/g, '<br>');
    }

    private handleDragMove(event: PointerEvent): void {
        if (!this.fabPointerActive) {
            return;
        }
        const dx = event.clientX - this.dragStartClientX;
        const dy = event.clientY - this.dragStartClientY;
        if (!this.isDragging && Math.hypot(dx, dy) < this.dragThresholdPx) {
            return;
        }

        if (!this.isDragging) {
            this.isDragging = true;
            this.dragMoved = true;
            this.assistantRootRef?.nativeElement?.classList.add('ai-assistant--dragging');
        }

        const pos = this.computeContainerPositionFromFab(
            this.dragOriginFabX + dx,
            this.dragOriginFabY + dy,
        );
        this.pendingDragLeft = pos.left;
        this.pendingDragTop = pos.top;
        this.pendingFabX = pos.fabX;
        this.pendingFabY = pos.fabY;
        this.applyTransformDirect(pos.left, pos.top);
    }

    private handleDragEnd(event: PointerEvent): void {
        this.removeDragListeners();

        if (!this.fabPointerActive) {
            return;
        }

        this.fabPointerActive = false;
        this.assistantRootRef?.nativeElement?.classList.remove('ai-assistant--dragging');

        this.zone.run(() => {
            this.isDragging = false;

            if (!this.dragMoved) {
                const dx = event.clientX - this.dragStartClientX;
                const dy = event.clientY - this.dragStartClientY;
                if (Math.hypot(dx, dy) < this.dragThresholdPx) {
                    this.onFabClick();
                }
            } else {
                this.positionX = this.pendingDragLeft;
                this.positionY = this.pendingDragTop;
                this.fabAnchorX = this.pendingFabX;
                this.fabAnchorY = this.pendingFabY;
                this.savePosition();
            }

            this.cdr.markForCheck();
        });
    }

    private removeDragListeners(): void {
        if (!this.dragListenersActive) {
            return;
        }
        document.removeEventListener('pointermove', this.onDocumentPointerMove);
        document.removeEventListener('pointerup', this.onDocumentPointerEnd);
        document.removeEventListener('pointercancel', this.onDocumentPointerEnd);
        this.dragListenersActive = false;
    }

    private focusInputSoon(): void {
        window.setTimeout(() => {
            this.inputFieldRef?.nativeElement?.focus();
            this.scrollMessagesSoon();
        }, 120);
    }

    private scrollMessagesSoon(): void {
        window.setTimeout(() => {
            const el = this.messageListRef?.nativeElement;
            if (el) {
                el.scrollTop = el.scrollHeight;
            }
        }, 80);
    }

    private initPosition(): void {
        const saved = this.readSavedFabAnchor();
        if (saved) {
            this.fabAnchorX = saved.x;
            this.fabAnchorY = saved.y;
            this.positionReady = true;
            requestAnimationFrame(() => {
                this.syncContainerPosition();
                this.cdr.markForCheck();
            });
            return;
        }
        this.setDefaultPosition();
        this.cdr.markForCheck();
    }

    private setDefaultPosition(): void {
        const root = this.assistantRootRef?.nativeElement;
        const fab = this.fabButtonRef?.nativeElement;
        if (!root || !fab) {
            return;
        }

        const fabRect = fab.getBoundingClientRect();
        const rootRect = root.getBoundingClientRect();
        this.layoutFabOffsetX = fabRect.left - rootRect.left;
        this.layoutFabOffsetY = fabRect.top - rootRect.top;
        this.layoutContainerW = rootRect.width;
        this.layoutContainerH = rootRect.height;

        this.fabAnchorX = fabRect.left;
        this.fabAnchorY = fabRect.top;
        this.positionReady = true;
        this.applyPosition(rootRect.left, rootRect.top, this.fabAnchorX, this.fabAnchorY);
    }

    /** Giữ FAB tại fabAnchor; dịch container khi panel/teaser thay đổi kích thước */
    private syncContainerPosition(): void {
        this.cacheLayoutMetrics();
        const pos = this.computeContainerPositionFromFab(this.fabAnchorX, this.fabAnchorY);
        this.applyPosition(pos.left, pos.top, pos.fabX, pos.fabY);
    }

    private cacheLayoutMetrics(): void {
        const root = this.assistantRootRef?.nativeElement;
        const fab = this.fabButtonRef?.nativeElement;
        if (!root || !fab) {
            return;
        }

        const rootRect = root.getBoundingClientRect();
        const fabRect = fab.getBoundingClientRect();
        this.layoutFabOffsetX = fabRect.left - rootRect.left;
        this.layoutFabOffsetY = fabRect.top - rootRect.top;
        this.layoutContainerW = rootRect.width;
        this.layoutContainerH = rootRect.height;
    }

    private computeContainerPositionFromFab(
        fabX: number,
        fabY: number,
    ): { left: number; top: number; fabX: number; fabY: number } {
        const clamped = this.clampContainerPosition(
            fabX - this.layoutFabOffsetX,
            fabY - this.layoutFabOffsetY,
        );
        return {
            left: clamped.left,
            top: clamped.top,
            fabX: clamped.left + this.layoutFabOffsetX,
            fabY: clamped.top + this.layoutFabOffsetY,
        };
    }

    private clampContainerPosition(left: number, top: number): { left: number; top: number } {
        const maxLeft = window.innerWidth - this.layoutContainerW - this.edgeMarginPx;
        const maxTop = window.innerHeight - this.layoutContainerH - this.edgeMarginPx;
        return {
            left: Math.max(this.edgeMarginPx, Math.min(left, maxLeft)),
            top: Math.max(this.edgeMarginPx, Math.min(top, maxTop)),
        };
    }

    private applyTransformDirect(left: number, top: number): void {
        const root = this.assistantRootRef?.nativeElement;
        if (root) {
            root.style.transform = `translate3d(${left}px, ${top}px, 0)`;
        }
    }

    private applyPosition(left: number, top: number, fabX?: number, fabY?: number): void {
        this.applyTransformDirect(left, top);
        this.positionX = left;
        this.positionY = top;
        if (fabX !== undefined) {
            this.fabAnchorX = fabX;
        }
        if (fabY !== undefined) {
            this.fabAnchorY = fabY;
        }
    }

    private readSavedFabAnchor(): { x: number; y: number } | null {
        try {
            const raw = localStorage.getItem(AI_ASSISTANT_POSITION_KEY);
            if (!raw) {
                return null;
            }
            const parsed = JSON.parse(raw) as { fabX?: number; fabY?: number };
            if (typeof parsed.fabX === 'number' && typeof parsed.fabY === 'number') {
                return { x: parsed.fabX, y: parsed.fabY };
            }
            return null;
        } catch {
            return null;
        }
    }

    private savePosition(): void {
        try {
            localStorage.setItem(
                AI_ASSISTANT_POSITION_KEY,
                JSON.stringify({ fabX: this.fabAnchorX, fabY: this.fabAnchorY }),
            );
        } catch {
            /* ignore */
        }
    }
}
